import { create } from 'zustand';
import type { EmailTemplate, UpdateEmailTemplateData } from '../types';
import { emailTemplateService } from '../services/emailTemplate.service';

interface EmailTemplateState {
  templates: EmailTemplate[];
  selectedTemplate: EmailTemplate | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTemplates: () => Promise<void>;
  getTemplateById: (id: string) => Promise<EmailTemplate>;
  updateTemplate: (id: string, data: UpdateEmailTemplateData) => Promise<void>;
  setSelectedTemplate: (template: EmailTemplate | null) => void;
  clearError: () => void;
}

export const useEmailTemplateStore = create<EmailTemplateState>((set) => ({
  templates: [],
  selectedTemplate: null,
  isLoading: false,
  error: null,

  fetchTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const templates = await emailTemplateService.getTemplates();
      set({ templates, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch templates';
      set({ error: message, isLoading: false });
    }
  },

  getTemplateById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const template = await emailTemplateService.getTemplateById(id);
      set({ selectedTemplate: template, isLoading: false });
      return template;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch template';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateTemplate: async (id: string, data: UpdateEmailTemplateData) => {
    set({ isLoading: true, error: null });
    try {
      const updatedTemplate = await emailTemplateService.updateTemplate(id, data);
      set((state) => ({
        templates: state.templates.map((t) => (t.id === id ? updatedTemplate : t)),
        selectedTemplate: state.selectedTemplate?.id === id ? updatedTemplate : state.selectedTemplate,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update template';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setSelectedTemplate: (template: EmailTemplate | null) => set({ selectedTemplate: template }),

  clearError: () => set({ error: null }),
}));

